import {Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FaultMonitoringDataService } from '../../services/fault-monitoring-data.service';
import { MonitoredTrain } from '../../models/monitored-train';
import { Observable, BehaviorSubject, merge, of, Subscription, interval } from 'rxjs';
import { switchMap, map, take } from 'rxjs/operators';
import { StateService, Transition } from '@uirouter/core';
import { MonitoringBehaviorSubjectService } from '../../services/monitoring-behavior-subject.service';
import { MonitoringAlarm } from '../../models/monitoring-alarm';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ProjectConfigurationBehaviorSubjectService } from '../../../project/services/project-configuration-behavior-subject.service';
import { ProjectConfiguration } from '../../../project/models/project-configuration';
import { environment } from '../../../../../environments/environment';
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { ReferenceArea } from '../../../reference-areas/models/reference-area';
import {Incident} from '../../../incidents/models/incident';


@Component({
  selector: 'app-monitoring-fault',
  template: require('./monitoring-fault.component.html'),
  styles: [require('./monitoring-fault.component.sass')]
})
export class MonitoringFaultComponent implements OnInit, OnDestroy {

  // monitoring data observables
  monitoredTrains: MonitoredTrain[] | null;
  monitoringAlarms: MonitoringAlarm[] | null;
  referenceAreas$: Observable<ReferenceArea[]>;
  selectedAlarmList: MonitoringAlarm[];
  polling$: Observable<number | undefined>;

  // project
  projectId: number;
  projectConfiguration: ProjectConfiguration;
  isIntegratedConsole: boolean = true;

  // Active view
  activeTab: string = 'tiles';

  // subscriptions
  monitoringDataSubscription: Subscription;
  pollingMonitoredTrainsSubscription: Subscription;
  projectConfigurationSubscription: Subscription;

  // Behavior subject to refresh observables
  private readonly refreshMonitoredTrainList$ = new BehaviorSubject(undefined);
  private readonly refreshReferenceAreas$ = new BehaviorSubject(undefined);
  private readonly refreshInterval$ = new BehaviorSubject(undefined);

  constructor(
    @Inject('MonitoringStoreService') private monitoringStoreService: any,
    @Inject('ConfigurationStoreService') private configurationStoreService: any,
    private faultMonitoringDataService: FaultMonitoringDataService,
    public $state: StateService,
    private trans: Transition,
    private monitoringBehaviorSubjectService: MonitoringBehaviorSubjectService,
    private projectConfigurationBehaviorSubjectService: ProjectConfigurationBehaviorSubjectService,
    private referenceAreaDataService: ReferenceAreaDataService,
    private messageService: MessageService,
    private translate: TranslateService,
    public dialogService: DialogService
  ) { 
    this.activeTab = this.trans.params().tab || 'tiles';
    this.projectId = this.$state.params && this.$state.params.projectId;
    this.isIntegratedConsole =  this.projectId === 0;

    this.referenceAreas$ = this.refreshReferenceAreas$.pipe(
      switchMap(() => this.referenceAreaDataService.getReferenceAreas(this.projectId)));

    this.polling$ = this.refreshInterval$.pipe(
      switchMap(() => {
        const monitoringFilters = localStorage.getItem('monitoring-filters-' + this.projectId);
        const autoRefresh = monitoringFilters && JSON.parse(monitoringFilters).autoRefresh;
        return autoRefresh? interval(environment.config.monitoring.refreshPeriod * 1000) : of(undefined).pipe(take(0))
      })
    )
   
    this.pollingMonitoredTrainsSubscription = merge(
      this.refreshMonitoredTrainList$, 
      this.polling$
      )
      .pipe(switchMap((data) => {
        this._getMonitoredTrainsFromStore();
        if(data === undefined) {
          return this.faultMonitoringDataService.getAllTrains(this.projectId);
        } else {
          const monitoringFilters = localStorage.getItem('monitoring-filters-' + this.projectId);
          const autoRefresh = monitoringFilters && JSON.parse(monitoringFilters).autoRefresh;
          if (autoRefresh) {
            return this.faultMonitoringDataService.getAllTrains(this.projectId);
          }
        }
        return of(this.monitoringStoreService.getUnfilteredTrains(this.projectId))
      })
    ).subscribe(monitoredTrains => {
        // store monitored trains data into the store
        this.monitoringStoreService.setTrains(monitoredTrains);
    });

    this.monitoringDataSubscription = this.monitoringBehaviorSubjectService.changeOccured.subscribe((data) => {
      this.refreshInterval$.next(undefined);
      if (data) {
        switch (data.action) {
          case 'trains-updated':
            setTimeout(() => this._getMonitoredTrainsFromStore(), 0);
          break;
          case 'reload-trains':
            this.reload();
          break;
        }
      }
    })
    this.projectConfigurationSubscription = this.projectConfigurationBehaviorSubjectService.changeOccured.subscribe((data: any) => {
      this.projectConfiguration = this.configurationStoreService.getProjectConfiguration(this.projectId);
    });
  }

  ngOnInit(): void {
    this.refreshInterval$.next(undefined);
  }


  _getMonitoredTrainsFromStore = () => {
    this.monitoredTrains = this.monitoringStoreService.getTrains(this.projectId);
    this.monitoringAlarms = this.monitoringStoreService.getAlarms(this.projectId);
  }

  investigateTrain = (train: MonitoredTrain) => {
    this.$state.go('monitoring-detail', {
      projectId: train.projectId, 
      trainId: train.trainId
    });
  }

  openTab = (tab: string) => {
    this.$state.go('monitoring', {
      projectId: this.projectId,
      tab
    }, {notify: false});
  }

  reload = () => {
    this.refreshMonitoredTrainList$.next(undefined);
    this.refreshReferenceAreas$.next(undefined);
  }

  acknowledgeAlarm = async (alarms: MonitoringAlarm[]) => {
    if (alarms && alarms.length > 0) {
      const trainId = alarms[0].trainId;
      const projectId = alarms[0].projectId;
      const eventIdsWithType = alarms.reduce((obj, alarm) => {
        obj[alarm.eventId] = alarm.type;
        return obj;
      }, {});

      try {
        await this.faultMonitoringDataService.acknowledgeAlarms(projectId, trainId, eventIdsWithType);
        this.selectedAlarmList = alarms.map(alarm => {
          return {
            ...alarm,
            acknowledged: true
          }
        })
      } catch (error) {
        
      }
    }
    this.refreshMonitoredTrainList$.next(undefined);
  }

  investigateAlarm = (alarms: MonitoringAlarm[]) => {
    if (alarms && alarms.length > 0) {
      const zabbixURL = alarms[0].zabbixURL;
      if (zabbixURL) {
          // responseData.message is Zabbix Config Full URL -> http://vmtrainzabbix01.ovh.21net.com/api_jsonrpc.php
          const matchedResult = zabbixURL.match(/^https?:\/\/[^#?\/]+(:?\/zabbix)*/);
          if (matchedResult) {
            const zabbixBaseURL = matchedResult[0];
            for(let i = 0; i < alarms.length; i++) {
              window.open(zabbixBaseURL + '/tr_events.php?triggerid=' + alarms[i].triggerId + '&eventid=' + alarms[i].eventId);
            }
          } else {
            this.messageService.add({
              severity:'error', 
              summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
              detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL')
            });
          }
  
      } else {
          this.messageService.add({
            severity:'error', 
            summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
            detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL_SELECTED_TRAIN')
          });
      }
    }
  }
  onRaiseIncident = (incident: Incident) => {
    this.reload();
  }


  ngOnDestroy(): void {
    this.monitoringDataSubscription.unsubscribe();
    this.projectConfigurationSubscription.unsubscribe();
    this.pollingMonitoredTrainsSubscription.unsubscribe();
  }
}
